import React, { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { getContentLanguage } from "../../services/LangPrefs";
import AppStoreBadgeLink from "../components/AppStoreBadgeLink";
import GooglePlayBadgeLink from "../components/GooglePlayBadgeLink";
import { usePagesTranslation } from "../locales";
import { getVisibleAndroidApps } from "./androidAppCatalog";
import LandingHeroPhoneMockup from "./LandingHeroPhoneMockup";

/** Google Play showcase: one glass card per Android title, above the web puzzles. */
export default function LandingAndroidAppsSection() {
    const { t } = usePagesTranslation();
    const contentLanguage = useMemo(() => getContentLanguage(), []);
    const apps = getVisibleAndroidApps();

    if (apps.length === 0) return null;

    return (
        <section className="pi-section" id="android-apps" aria-labelledby="pi-android-apps-title">
            <div className="pi-landing__inner">
                <div className="pi-hero pi-apps-showcase">
                    <header className="pi-section__head">
                        <p className="pi-section__kicker">
                            {t("landingApp.apps-kicker", "On iPhone and Android")}
                        </p>
                        <h2 className="pi-section__title" id="pi-android-apps-title">
                            {t("landingApp.apps-title", "Our Mobile Apps")}
                        </h2>
                        <p className="pi-section__subtitle">
                            {t(
                                "landingApp.apps-subtitle",
                                "Free to install, built for one-handed play, and they keep your progress in sync with the web.",
                            )}
                        </p>
                        <p className="pi-apps-premium-note">
                            {t(
                                "landingApp.apps-premium-note",
                                "Become a web Premium member and use all the apps.",
                            )}{" "}
                            <RouterLink className="pi-apps-premium-note__link" to="/howto/app-login">
                                {t("landingApp.apps-premium-readme", "Read me")}
                            </RouterLink>
                        </p>
                    </header>
                    <div className="pi-hero__visual">
                        <LandingHeroPhoneMockup
                            contentLanguage={contentLanguage}
                            mediaAlt={t(
                                "landingApp.hero-phone-alt",
                                "Puzzle Interact gameplay preview on a phone",
                            )}
                        />
                    </div>
                </div>

                <div className="pi-grid">
                    {apps.map((app) => (
                        <article className="pi-card" key={app.id}>
                            <div className="pi-app__head">
                                <img
                                    className="pi-app__icon"
                                    src={app.iconUrl}
                                    alt=""
                                    width="68"
                                    height="68"
                                    loading="lazy"
                                    decoding="async"
                                />
                                <div>
                                    <h3 className="pi-app__title">{app.name}</h3>
                                    <span className={`pi-badge pi-badge--${app.badgeTheme}`}>
                                        {t(
                                            `landingApp.app-${app.id}-badge`,
                                            app.badge,
                                        )}
                                    </span>
                                </div>
                            </div>
                            <p className="pi-app__desc">
                                {t(app.descriptionKey, app.description)}
                            </p>
                            <div className="pi-card__spacer" />
                            <div className="pi-app__stores pi-app__stores--center">
                                {app.appStoreUrl ? (
                                    <AppStoreBadgeLink
                                        href={app.appStoreUrl}
                                        height={40}
                                        ariaLabel={t(
                                            "landing.word-wheel-app-store-badge-aria",
                                            "Get Word Wheel Quest on the App Store (opens in a new tab)",
                                        )}
                                    />
                                ) : null}
                                {app.playUrl ? (
                                    <GooglePlayBadgeLink
                                        height={40}
                                        href={app.playUrl}
                                        ariaLabel={t(
                                            app.id === "jigsaw"
                                                ? "landing.jigsaw-google-play-badge-aria"
                                                : app.id === "sliding"
                                                    ? "landing.google-play-badge-aria"
                                                    : "landing.word-wheel-google-play-badge-aria",
                                            `Get ${app.name} on Google Play (opens in a new tab)`,
                                        )}
                                    />
                                ) : null}
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}
